/**
 * Entry-time validation for the Operation screen's Total machine hours field,
 * mirroring the backend's InspectionCategoryTypeDtoValidator rules (#9823/#9824)
 * so the inspector hears about an implausible reading while still on the screen
 * instead of via a submit error at the end of the inspection (#10549).
 */

/** Mirrors the backend's MaxPlausibleMachineHoursPerDay. */
export const MAX_PLAUSIBLE_MACHINE_HOURS_PER_DAY = 24;

export interface TotalMachineHoursEvaluation {
  isValid: boolean;
  message?: string;
}

const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

/**
 * Validates a Total machine hours reading against the previous/next inspections'
 * readings. Any bound that is unknown on this device is skipped (hours are
 * optional and the device may not hold the neighbouring inspections) — the
 * backend re-validates authoritatively at submit.
 */
export function evaluateTotalMachineHours({
  value,
  inspectionDate,
  lastTotalHours,
  lastTotalHoursDate,
  nextTotalHours,
}: {
  /** The entered reading. */
  value: number;
  /** The current inspection's date (for the hours-per-day plausibility rule). */
  inspectionDate?: Date | null;
  /** The previous inspection's reading, when known on this device. */
  lastTotalHours?: number;
  /** The date of the inspection that carried lastTotalHours. */
  lastTotalHoursDate?: Date;
  /** The next (later) inspection's reading, when editing a reopened inspection. */
  nextTotalHours?: number;
}): TotalMachineHoursEvaluation {
  if (Number.isNaN(value)) {
    return { isValid: true }; // nothing entered yet — emptiness is handled elsewhere
  }

  if (lastTotalHours !== undefined) {
    // Backend rule 1: hour meters do not go backwards.
    if (value < lastTotalHours) {
      return {
        isValid: false,
        message: `Total machine hours cannot be less than ${lastTotalHours}`,
      };
    }

    // Backend rule 2: a reading implying more than 24 running hours per day
    // since the previous reading cannot be a valid meter reading.
    if (lastTotalHoursDate && inspectionDate) {
      const daysBetween =
        (inspectionDate.getTime() - lastTotalHoursDate.getTime()) /
        (24 * 60 * 60 * 1000);
      if (daysBetween > 0) {
        const impliedHoursPerDay = (value - lastTotalHours) / daysBetween;
        if (impliedHoursPerDay > MAX_PLAUSIBLE_MACHINE_HOURS_PER_DAY) {
          return {
            isValid: false,
            message:
              `Total machine hours (${value}) implies ${impliedHoursPerDay.toFixed(1)} h/day since the previous ` +
              `inspection (${lastTotalHours} on ${formatDate(lastTotalHoursDate)}). The maximum possible is ` +
              `${MAX_PLAUSIBLE_MACHINE_HOURS_PER_DAY} h/day - please re-check the meter reading.`,
          };
        }
      }
    }
  }

  if (nextTotalHours !== undefined && value > nextTotalHours) {
    return {
      isValid: false,
      message: `Total machine hours cannot be greater than ${nextTotalHours}`,
    };
  }

  return { isValid: true };
}
